import React, { useEffect, useState } from 'react';
import { useAgent } from '../../context/AgentContext';
import {
  Plus,
  Bot,
  MessageSquare,
  FileText,
  Briefcase,
  Users,
  Scale,
  ShieldCheck,
  Settings2
} from 'lucide-react';
import CreateAgentModal from '../agents/CreateAgentModal';
import './Sidebar.css';

const agentIcons = {
  general: Bot,
  finance: Briefcase,
  hr: Users,
  legal: Scale,
  compliance: ShieldCheck,
  custom: Settings2
};

const agentColors = {
  general: '#3B82F6',
  finance: '#10B981',
  hr: '#8B5CF6',
  legal: '#F59E0B',
  compliance: '#EF4444',
  custom: '#6B7280'
};

const Sidebar = () => {
  const {
    agents,
    selectedAgent,
    conversations,
    fetchAgents,
    selectAgent,
    createConversation,
    loadConversation,
    setActiveConversation,
    loading
  } = useAgent();

  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleNewConversation = async () => {
    if (selectedAgent) {
      await createConversation(selectedAgent._id);
    }
  };

  const handleConversationClick = async (conv) => {
    await loadConversation(conv._id);
  };

  const getIcon = (type) => {
    const Icon = agentIcons[type] || Bot;
    return Icon;
  };

  return (
    <aside className="sidebar">
      {/* Agents Section */}
      <div className="sidebar-section">
        <div className="sidebar-header">
          <h3>AI Agents</h3>
          <button
            className="btn btn-icon btn-ghost"
            onClick={() => setShowCreateModal(true)}
            title="Create new agent"
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="agents-list">
          {loading.agents ? (
            <div className="sidebar-loading">
              <div className="spinner" />
            </div>
          ) : agents.length === 0 ? (
            <div className="sidebar-empty">
              <p>No agents yet</p>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Create your first agent
              </button>
            </div>
          ) : (
            agents.map((agent) => {
              const Icon = getIcon(agent.type);
              const isSelected = selectedAgent?._id === agent._id;

              return (
                <button
                  key={agent._id}
                  className={`agent-item ${isSelected ? 'active' : ''}`}
                  onClick={() => selectAgent(agent)}
                >
                  <div
                    className="agent-icon"
                    style={{ backgroundColor: agent.color || agentColors[agent.type] }}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="agent-info">
                    <span className="agent-name">{agent.name}</span>
                    <span className="agent-type">{agent.type}</span>
                  </div>
                  <span className="agent-docs">{agent.stats?.documentCount || 0} docs</span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Conversations Section */}
      {selectedAgent && (
        <div className="sidebar-section sidebar-section-grow">
          <div className="sidebar-header">
            <h3>Conversations</h3>
            <button
              className="btn btn-icon btn-ghost"
              onClick={handleNewConversation}
              title="New conversation"
            >
              <Plus size={18} />
            </button>
          </div>

          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="sidebar-empty">
                <MessageSquare size={24} className="text-gray-400" />
                <p>No conversations yet</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv._id}
                  className="conversation-item"
                  onClick={() => handleConversationClick(conv)}
                >
                  <MessageSquare size={16} />
                  <div className="conversation-info">
                    <span className="conversation-title">{conv.title}</span>
                    <span className="conversation-date">
                      {new Date(conv.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Documents quick access */}
      {selectedAgent && (
        <div className="sidebar-footer">
          <div className="sidebar-stat">
            <FileText size={16} />
            <span>{selectedAgent.stats?.documentCount || 0} documents</span>
          </div>
          <div className="sidebar-stat">
            <MessageSquare size={16} />
            <span>{selectedAgent.stats?.queryCount || 0} queries</span>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateAgentModal onClose={() => setShowCreateModal(false)} />
      )}
    </aside>
  );
};

export default Sidebar;
